import BRPropertyModel from '../Property/BRPropertyModel';
import { BrCatastropheLimitType, SubscriberResource, UnderwriterReview } from 'types/underwriting.type';
import { formatDollar, unformatNumberString } from 'helpers/formatters';

const isNullOrUndefinedOrBlank = (value: string | number | undefined) => {
    return value === null || value === undefined || value === '';
};

/**
 * Returns the combined capacity of all participating subscribers
 */
export const getCombinedSubscriberCapacity = (subscribers?: SubscriberResource[]): number | undefined => {
    if (!subscribers || subscribers.length === 0) return 0;
    return subscribers.filter((s) => s.isParticipating).reduce((sum, s) => sum + (s.capacity ?? 0), 0);
};

// Function used to validate a top level BR review
// combinedSubscriberCapacity: optional sum of capacities for all participating subscribers
export const validateBrUnderwriterReview = (review: any, combinedSubscriberCapacity?: number): any => {
    // Construct an object where keys are formik field names and values are messages indicating error.
    // If no error, the value should be null or undefined.
    const errors: any = {};
    if (!review) return errors;

    // Check required fields
    const requiredFields = ['isLeader', 'policyLimitOfLiability', 'lumenLimitOfLiability'];
    requiredFields.forEach((field) => {
        if (review[field] === null || review[field] === undefined || review[field] === '' || review[field] === 0) {
            errors[field] = `${field} is required`;
        }
    });

    // Validate lumenLimitOfLiability does not exceed combined participating subscriber capacity
    if (
        review.lumenLimitOfLiability &&
        unformatNumberString(review.lumenLimitOfLiability) >
            (combinedSubscriberCapacity ? combinedSubscriberCapacity : 0)
    ) {
        errors.lumenLimitOfLiability = `Lumen Limit exceeds the capacity of subscribers (${formatDollar(
            combinedSubscriberCapacity || 0,
            0
        )})`;
    }

    return errors;
};

export const validateLocationReview = (review: BRPropertyModel): { [key: string]: string } => {
    // Construct an object where keys are formik field names and values are messages indicating error.
    // If no error, the value should be null or undefined.
    const errors: any = {};
    if (!review) return errors;

    // Check required fields
    const requiredFields = [
        'catastropheLimitType',
        'deductible',
        'periodOfIndemnity',
        'waitingPeriod',
        'hasSoftCost30DayLimit',
        'hasRentalIncome30DayLimit',
        'hasIncome30DayLimit',
        'baseRate',
        'finalRate',
        'triaPercentage',
        'isWaterDamageCovered',
        'isEarthMovementCovered',
        'isFloodCovered',
    ];
    requiredFields.forEach((field) => {
        if (review[field] === null || review[field] === undefined || review[field] === '') {
            errors[field] = `${field} is required`;
        }
    });

    // Check conditionally required fields

    // 30 day limits
    // If 30 day limit box is checked, amount is required
    if (review.hasSoftCost30DayLimit && isNullOrUndefinedOrBlank(review.softCost30DayLimitAmount)) {
        errors.softCost30DayLimitAmount = 'Soft Cost 30 Day Limit Amount is marked as required';
    }
    // If 30 day limit value is entered, box must be checked
    if (!isNullOrUndefinedOrBlank(review.softCost30DayLimitAmount) && !review.hasSoftCost30DayLimit) {
        errors.hasSoftCost30DayLimit = 'Soft Cost 30 Day Limit checkbox must be checked if an amount is entered';
    }
    // If 30 day limit box is checked, amount is required
    if (review.hasRentalIncome30DayLimit && isNullOrUndefinedOrBlank(review.rentalIncome30DayLimitAmount)) {
        errors.rentalIncome30DayLimitAmount = 'Rental Income 30 Day Limit Amount is marked as required';
    }
    // If 30 day limit value is entered, box must be checked
    if (!isNullOrUndefinedOrBlank(review.rentalIncome30DayLimitAmount) && !review.hasRentalIncome30DayLimit) {
        errors.hasRentalIncome30DayLimit =
            'Rental Income 30 Day Limit checkbox must be checked if an amount is entered';
    }
    // If 30 day limit box is checked, amount is required
    if (review.hasIncome30DayLimit && isNullOrUndefinedOrBlank(review.income30DayLimitAmount)) {
        errors.income30DayLimitAmount = 'Income 30 Day Limit Amount is marked as required';
    }
    // If 30 day limit value is entered, box must be checked
    if (!isNullOrUndefinedOrBlank(review.income30DayLimitAmount) && !review.hasIncome30DayLimit) {
        errors.hasIncome30DayLimit = 'Income 30 Day Limit checkbox must be checked if an amount is entered';
    }

    // Combined/separate catastrophe limits
    if (
        review.catastropheLimitType === BrCatastropheLimitType.COMBINED &&
        isNullOrUndefinedOrBlank(review.combinedCatastropheLimit)
    ) {
        errors.combinedCatastropheLimit = 'Combined Catastrophe Limit is required';
    }
    if (review.catastropheLimitType === BrCatastropheLimitType.SEPARATE) {
        if (isNullOrUndefinedOrBlank(review.brCatastropheLimit)) {
            errors.brCatastropheLimit = 'Building/Property Catastrophe Limit is required';
        }
        if (isNullOrUndefinedOrBlank(review.delayInCompletionCatastropheLimit)) {
            errors.delayInCompletionCatastropheLimit = 'Delay In Completion Catastrophe Limit is required';
        }
    }

    // Base rate/modeled rate
    // Note - modeled rate is not currently used in BR, so this validation is commented out
    // but left here for future reference
    // if (isNullOrUndefinedOrBlank(review.baseRate) && isNullOrUndefinedOrBlank(review.modeled)) {
    //     errors.modeled = 'Modeled rate is required if Base Rate is not provided';
    // }

    // Sublimits for peril coverages
    if (review.hasWaterDamageSublimit && isNullOrUndefinedOrBlank(review.waterDamageSublimit)) {
        errors.waterDamageSublimit = 'Water Damage Sublimit is required';
    }
    if (review.hasEarthMovementSublimit && isNullOrUndefinedOrBlank(review.earthMovementSublimit)) {
        errors.earthMovementSublimit = 'Earth Movement Sublimit is required';
    }
    if (review.hasFloodSublimit && isNullOrUndefinedOrBlank(review.floodSublimit)) {
        errors.floodSublimit = 'Flood Sublimit is required';
    }
    if (review.hasWindstormSublimit && isNullOrUndefinedOrBlank(review.windstormSublimit)) {
        errors.windstormSublimit = 'Windstorm Sublimit is required';
    }
    if (review.hasNamedWindstormSublimit && isNullOrUndefinedOrBlank(review.namedWindstormSublimit)) {
        errors.namedWindstormSublimit = 'Named Windstorm Sublimit is required';
    }
    // Sublimit box must be checked if a value is entered
    if (!isNullOrUndefinedOrBlank(review.waterDamageSublimit) && !review.hasWaterDamageSublimit) {
        errors.hasWaterDamageSublimit = 'Water Damage Sublimit checkbox must be checked if a sublimit is entered';
    }
    if (!isNullOrUndefinedOrBlank(review.earthMovementSublimit) && !review.hasEarthMovementSublimit) {
        errors.hasEarthMovementSublimit = 'Earth Movement Sublimit checkbox must be checked if a sublimit is entered';
    }
    if (!isNullOrUndefinedOrBlank(review.floodSublimit) && !review.hasFloodSublimit) {
        errors.hasFloodSublimit = 'Flood Sublimit checkbox must be checked if a sublimit is entered';
    }
    if (!isNullOrUndefinedOrBlank(review.windstormSublimit) && !review.hasWindstormSublimit) {
        errors.hasWindstormSublimit = 'Windstorm Sublimit checkbox must be checked if a sublimit is entered';
    }
    if (!isNullOrUndefinedOrBlank(review.namedWindstormSublimit) && !review.hasNamedWindstormSublimit) {
        errors.hasNamedWindstormSublimit = 'Named Windstorm Sublimit checkbox must be checked if a sublimit is entered';
    }

    // Deductibles for peril coverages
    if (review.isWaterDamageCovered && isNullOrUndefinedOrBlank(review.waterDamageDeductible)) {
        errors.waterDamageDeductible = 'Water Damage Deductible is required';
    }
    if (review.isEarthMovementCovered && isNullOrUndefinedOrBlank(review.earthMovementDeductible)) {
        errors.earthMovementDeductible = 'Earth Movement Deductible is required';
    }
    if (review.isFloodCovered && isNullOrUndefinedOrBlank(review.floodDeductible)) {
        errors.floodDeductible = 'Flood Deductible is required';
    }
    if (review.isWindstormCovered && isNullOrUndefinedOrBlank(review.windstormDeductible)) {
        errors.windstormDeductible = 'Windstorm Deductible is required';
    }
    if (review.isNamedWindstormCovered && isNullOrUndefinedOrBlank(review.namedWindstormDeductible)) {
        errors.namedWindstormDeductible = 'Named Windstorm Deductible is required';
    }

    return errors;
};
